import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_CODE_RE = /^\+\d{1,4}$/;
const PHONE_RE = /^\d{7,15}$/;

const ROLES = [
  { value: 'professional', label: 'Professional' },
  { value: 'home_owner', label: 'Homeowner' },
];

const ContactFormModal = ({ isOpen, onClose, chatId, inquiryType }) => {
  const [status, setStatus] = useState('idle');
  const [form, setForm] = useState({
    user_fullname: '',
    user_email: '',
    phone_code: '+1',
    user_phone: '',
    user_role: '',
  });
  const [errors, setErrors] = useState({});
  const [roleOpen, setRoleOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setStatus('loading');
    setForm({ user_fullname: '', user_email: '', phone_code: '+1', user_phone: '', user_role: '' });
    setErrors({});
    setRoleOpen(false);

    appAxios
      .get('/ContactForm/v1.0/get-contact-form', { params: { chat_id: chatId } })
      .then(res => {
        const d = res.data;
        if (d?.success && d?.data) {
          setStatus('already_submitted');
        } else {
          setStatus('form');
        }
      })
      .catch(() => {
        setStatus('form');
      });
  }, [isOpen, chatId]);

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setRoleOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isOpen) return null;

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.user_fullname.trim()) e.user_fullname = 'Full name is required.';
    if (!EMAIL_RE.test(form.user_email.trim())) e.user_email = 'Enter a valid email address.';
    if (!PHONE_CODE_RE.test(form.phone_code.trim())) e.phone_code = 'Enter a valid country code (e.g. +1).';
    if (!PHONE_RE.test(form.user_phone.replace(/\s/g, ''))) e.user_phone = 'Enter a valid phone number.';
    if (!form.user_role) e.user_role = 'Please select a role.';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setStatus('submitting');
    try {
      await appAxios.post('/ContactForm/v1.0/create-contact-form', {
        chat_id: chatId,
        user_fullname: form.user_fullname.trim(),
        user_email: form.user_email.trim(),
        phone_code: form.phone_code.trim(),
        user_phone: form.user_phone.replace(/\s/g, ''),
        user_role: form.user_role,
        inquiry_type: inquiryType
      });
      toast.success('Contact form submitted successfully!');
      setStatus('success');
      onClose();
    } catch (err) {
      console.error('Contact form submission error:', err);
      toast.error('Failed to submit. Please try again.');
      setStatus('form');
    }
  };

  const selectedRoleLabel = ROLES.find(r => r.value === form.user_role)?.label ?? '';

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 font-baloo-400 ">
      <div className="bg-[#1a1a1a] rounded-lg p-8 max-w-md w-full mx-4 relative flex flex-col items-center">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-semibold text-white text-center mt-3 mb-2">Contact Form</h2>
        <p className="text-gray-400 text-center text-sm mb-6 max-w-xs">
          Just a few quick details and we&apos;ll take it from there.
        </p>

        {/* Loading */}
        {status === 'loading' && (
          <div className="flex items-center gap-1.5 py-8">
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" />
          </div>
        )}

        {/* Already submitted */}
        {(status === 'already_submitted' || status === 'success') && (
          <p className="text-gray-300 text-center text-sm py-8">
            You have already submitted the contact form, we will reach out to you soon.
          </p>
        )}

        {/* Form */}
        {(status === 'form' || status === 'submitting') && (
          <div className="w-full flex flex-col gap-3">
            {/* Full Name */}
            <div>
              <input
                type="text"
                placeholder="Full Name"
                value={form.user_fullname}
                onChange={e => set('user_fullname', e.target.value)}
                className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg placeholder-gray-500 focus:outline-none focus:border-gray-500"
              />
              {errors.user_fullname && <p className="text-red-400 text-xs mt-1 ml-1">{errors.user_fullname}</p>}
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                placeholder="Email Address"
                value={form.user_email}
                onChange={e => set('user_email', e.target.value)}
                className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg placeholder-gray-500 focus:outline-none focus:border-gray-500"
              />
              {errors.user_email && <p className="text-red-400 text-xs mt-1 ml-1">{errors.user_email}</p>}
            </div>

            {/* Phone */}
            <div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.phone_code}
                  onChange={e => set('phone_code', e.target.value)}
                  className="w-20 bg-black border border-gray-700 text-white px-3 py-3 rounded-lg text-center placeholder-gray-500 focus:outline-none focus:border-gray-500"
                  maxLength={5}
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.user_phone}
                  onChange={e => set('user_phone', e.target.value.replace(/[^\d\s]/g, ''))}
                  className="flex-1 bg-black border border-gray-700 text-white px-4 py-3 rounded-lg placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
              {(errors.phone_code || errors.user_phone) && (
                <p className="text-red-400 text-xs mt-1 ml-1">{errors.phone_code || errors.user_phone}</p>
              )}
            </div>

            {/* Role Dropdown */}
            <div ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setRoleOpen(prev => !prev)}
                className={`w-full bg-black border text-left px-4 py-3 rounded-lg flex items-center justify-between focus:outline-none ${errors.user_role ? 'border-red-500' : 'border-gray-700 hover:border-gray-500'}`}
              >
                <span className={selectedRoleLabel ? 'text-white' : 'text-gray-500'}>
                  {selectedRoleLabel || 'I am a …'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${roleOpen ? 'rotate-180' : ''}`} />
              </button>

              {roleOpen && (
                <div className="mt-1 bg-black border border-gray-700 rounded-lg overflow-hidden">
                  {ROLES.map(r => (
                    <label
                      key={r.value}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        checked={form.user_role === r.value}
                        onChange={() => {
                          set('user_role', form.user_role === r.value ? '' : r.value);
                          setRoleOpen(false);
                        }}
                        className="w-4 h-4 accent-white"
                      />
                      <span className="text-white text-sm">{r.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {errors.user_role && <p className="text-red-400 text-xs mt-1 ml-1">{errors.user_role}</p>}
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={status === 'submitting'}
              className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg mt-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {status === 'submitting' ? 'Submitting…' : 'Continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactFormModal;
